import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Plus, Pencil, Trash2, Star, StarOff, Eye, EyeOff, GripVertical } from "lucide-react";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { useAnnouncements, type Announcement, type CreateAnnouncementData } from "@/hooks/useAnnouncements";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const typeLabels = {
  welcome: "Boas-vindas",
  feature: "Funcionalidade",
  tip: "Dica",
  update: "Novidade",
  highlight: "Destaque",
};

const AnnouncementForm = ({ 
  announcement, 
  onSubmit, 
  onClose,
  isLoading 
}: { 
  announcement?: Announcement;
  onSubmit: (data: CreateAnnouncementData) => void;
  onClose: () => void;
  isLoading: boolean;
}) => {
  const [formData, setFormData] = useState<CreateAnnouncementData>({
    title: announcement?.title || '',
    content: announcement?.content || '',
    type: announcement?.type || 'update',
    video_url: announcement?.video_url || '',
    image_url: announcement?.image_url || '',
    button_text: announcement?.button_text || '',
    button_link: announcement?.button_link || '',
    order_position: announcement?.order_position || 0,
    is_featured: announcement?.is_featured || false,
    is_active: announcement?.is_active ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Título</Label>
        <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: 🎉 Nova Funcionalidade!" required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Conteúdo (Markdown suportado)</Label>
        <Textarea id="content" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} placeholder="Use **texto** para negrito, • para listas..." rows={8} required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="type">Tipo</Label>
          <Select value={formData.type} onValueChange={(value: 'update' | 'feature' | 'tip' | 'welcome' | 'highlight') => setFormData({ ...formData, type: value })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="highlight">Destaque</SelectItem>
              <SelectItem value="welcome">Boas-vindas</SelectItem>
              <SelectItem value="feature">Funcionalidade</SelectItem>
              <SelectItem value="tip">Dica</SelectItem>
              <SelectItem value="update">Novidade</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="order">Posição</Label>
          <Input id="order" type="number" value={formData.order_position} onChange={(e) => setFormData({ ...formData, order_position: parseInt(e.target.value) || 0 })} min={0} />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="video_url">URL do Vídeo</Label>
        <Input id="video_url" value={formData.video_url || ''} onChange={(e) => setFormData({ ...formData, video_url: e.target.value || null })} placeholder="https://youtube.com/watch?v=..." />
      </div>
      <div className="space-y-2">
        <Label htmlFor="image_url">URL da Imagem</Label>
        <Input id="image_url" value={formData.image_url || ''} onChange={(e) => setFormData({ ...formData, image_url: e.target.value || null })} placeholder="https://..." />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="button_text">Texto do Botão (opcional)</Label>
          <Input id="button_text" value={formData.button_text || ''} onChange={(e) => setFormData({ ...formData, button_text: e.target.value || null })} placeholder="Ex: Acessar agora" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="button_link">Link do Botão (opcional)</Label>
          <Input id="button_link" value={formData.button_link || ''} onChange={(e) => setFormData({ ...formData, button_link: e.target.value || null })} placeholder="Ex: /candidate/community" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Switch id="is_featured" checked={formData.is_featured} onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })} />
          <Label htmlFor="is_featured">Destaque</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch id="is_active" checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
          <Label htmlFor="is_active">Ativo</Label>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button type="submit" disabled={isLoading}>{announcement ? 'Salvar' : 'Criar'}</Button>
      </div>
    </form>
  );
};

const Announcements = () => {
  const { announcements, isLoading, createAnnouncement, updateAnnouncement, deleteAnnouncement, isCreating, isUpdating } = useAnnouncements();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);

  const handleCreate = (data: CreateAnnouncementData) => { createAnnouncement(data); setIsCreateOpen(false); };
  const handleUpdate = (data: CreateAnnouncementData) => { if (editingAnnouncement) { updateAnnouncement({ id: editingAnnouncement.id, ...data }); setEditingAnnouncement(null); } };
  const toggleFeatured = (announcement: Announcement) => { updateAnnouncement({ id: announcement.id, is_featured: !announcement.is_featured }); };
  const toggleActive = (announcement: Announcement) => { updateAnnouncement({ id: announcement.id, is_active: !announcement.is_active }); };

  return (
    <>
      <Helmet><title>Gerenciar Anúncios - Admin</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <CandidateLayout maxWidth="6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Central de Novidades</h1>
            <p className="text-muted-foreground">Gerencie os anúncios da página "Comece Aqui"</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Novo Anúncio</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Criar Anúncio</DialogTitle></DialogHeader>
              <AnnouncementForm onSubmit={handleCreate} onClose={() => setIsCreateOpen(false)} isLoading={isCreating} />
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => (<Skeleton key={i} className="h-24 w-full" />))}</div>
        ) : (
          <div className="space-y-4">
            {announcements?.map((announcement) => (
              <Card key={announcement.id} className={`${!announcement.is_active ? 'opacity-50' : ''} ${announcement.is_featured ? 'border-primary' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab" />
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base">{announcement.title}</CardTitle>
                          <Badge variant="outline" className="text-xs">{typeLabels[announcement.type]}</Badge>
                          {announcement.is_featured && <Badge className="text-xs">Destaque</Badge>}
                          {!announcement.is_active && <Badge variant="secondary" className="text-xs">Inativo</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{announcement.content.substring(0, 100)}...</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => toggleFeatured(announcement)}>{announcement.is_featured ? <StarOff className="h-4 w-4" /> : <Star className="h-4 w-4" />}</Button>
                      <Button variant="ghost" size="icon" onClick={() => toggleActive(announcement)}>{announcement.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                      <Dialog open={editingAnnouncement?.id === announcement.id} onOpenChange={(open) => !open && setEditingAnnouncement(null)}>
                        <DialogTrigger asChild><Button variant="ghost" size="icon" onClick={() => setEditingAnnouncement(announcement)}><Pencil className="h-4 w-4" /></Button></DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader><DialogTitle>Editar Anúncio</DialogTitle></DialogHeader>
                          <AnnouncementForm announcement={announcement} onSubmit={handleUpdate} onClose={() => setEditingAnnouncement(null)} isLoading={isUpdating} />
                        </DialogContent>
                      </Dialog>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Excluir anúncio?</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => deleteAnnouncement(announcement.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
            {announcements?.length === 0 && <Card><CardContent className="py-8 text-center text-muted-foreground">Nenhum anúncio cadastrado.</CardContent></Card>}
          </div>
        )}
      </CandidateLayout>
    </>
  );
};

export default Announcements;
